import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, RotateCcw, Sliders, ArrowRight, EyeOff, Eye, Search, X as XIcon } from "lucide-react";

/**
 * Iter-189 · Super-Admin Business-Type override panel.
 *
 * Renders on the Entity Detail page. Shows:
 *   • Base business type dropdown (5 founder-signed options)
 *   • Distribution + Dealership add-on switches
 *   • Per-feature override grid (191 codes, grouped by section) — each row
 *     shows the base applicability + a Y/N override switch. Highest
 *     precedence over the founder-signed matrix.
 *
 * Backend routes wired:
 *   GET  /api/business-type-features/matrix
 *   PATCH /api/admin/tenants/:org_id/business-type
 */
export default function BusinessTypeOverridePanel({ orgId, initial, onSaved }) {
  const [matrix, setMatrix] = useState(null);      // full grid (all BTs)
  const [bt, setBt] = useState(initial?.business_type || "trading_with_inventory");
  const [isDealer, setIsDealer] = useState(!!initial?.is_dealership);
  const [isDistro, setIsDistro] = useState(!!initial?.is_distribution);
  const [overrides, setOverrides] = useState({});
  const [effective, setEffective] = useState({});
  // Iter-190 · Snapshot the effective state at load time so the preview
  // banner can show a clean before → after diff on every edit.
  const [initialEffective, setInitialEffective] = useState({});
  // Iter-195 · Feature search — filter the 191-row grid by code / name
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [m, eff] = await Promise.all([
      api.get("/business-type-features/matrix"),
      api.get(`/admin/tenants/${orgId}`),
    ]);
    setMatrix(m.data);
    // Re-derive current effective state from the org doc + matrix
    const org = eff.data?.org || {};
    const loadedBt   = org.business_type || "trading_with_inventory";
    const loadedDlr  = !!org.is_dealership;
    const loadedDist = !!org.is_distribution;
    const loadedOv   = org.business_type_feature_overrides || {};
    setBt(loadedBt);
    setIsDealer(loadedDlr);
    setIsDistro(loadedDist);
    setOverrides(loadedOv);
    // Snapshot initial effective for the preview diff banner
    const snap = {};
    (m.data.features || []).forEach(row => {
      let on = !!row[loadedBt];
      if (loadedDlr && row.dealership) on = true;
      if (loadedDist && row.distribution && !on) on = true;
      if (Object.prototype.hasOwnProperty.call(loadedOv, row.code)) on = !!loadedOv[row.code];
      snap[row.code] = on;
    });
    setInitialEffective(snap);
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  // Compute the effective ON/OFF for each code (mirrors backend logic)
  useEffect(() => {
    if (!matrix) return;
    const eff = {};
    (matrix.features || []).forEach(row => {
      let on = !!row[bt];
      if (isDealer && row.dealership) on = true;
      if (isDistro && row.distribution && !on) on = true;
      if (Object.prototype.hasOwnProperty.call(overrides, row.code)) {
        on = !!overrides[row.code];
      }
      eff[row.code] = on;
    });
    setEffective(eff);
  }, [matrix, bt, isDealer, isDistro, overrides]);

  const toggleOverride = (code, currentEffective) => {
    setOverrides(prev => {
      const next = { ...prev };
      if (Object.prototype.hasOwnProperty.call(next, code)) {
        // Cycle: override→clear (back to matrix default)
        delete next[code];
      } else {
        // Set to opposite of current effective (i.e. explicit override)
        next[code] = !currentEffective;
      }
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/tenants/${orgId}/business-type`, {
        business_type: bt,
        is_dealership: isDealer,
        is_distribution: isDistro,
        business_type_feature_overrides: overrides,
      });
      toast.success("Business-type + overrides saved");
      // Iter-190 · Refresh the "before" snapshot so the diff banner
      // clears until the next edit.
      setInitialEffective({ ...effective });
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const resetOverrides = () => {
    setOverrides({});
    toast.info("Overrides cleared — save to apply");
  };

  // Iter-190 · Effective-features diff — before (persisted) vs after (edited).
  // Rolls up per-feature changes into section-level exposure/hide counts so
  // the super-admin can see exactly what will change BEFORE hitting save.
  // Kept ABOVE the `if (!matrix) return null` guard so hook order is stable.
  const diff = useMemo(() => {
    if (!matrix) return { exposedCodes: [], hiddenCodes: [], secExposed: [], secHidden: [], total: 0 };
    const exposedCodes = [], hiddenCodes = [];
    (matrix.features || []).forEach(f => {
      const was = !!initialEffective[f.code];
      const now = !!effective[f.code];
      if (was === now) return;
      const item = { code: f.code, name: f.name, section: f.section };
      if (now) exposedCodes.push(item); else hiddenCodes.push(item);
    });
    const beforeSec = {}, afterSec = {};
    (matrix.features || []).forEach(f => {
      beforeSec[f.section] = (beforeSec[f.section] || false) || !!initialEffective[f.code];
      afterSec[f.section]  = (afterSec[f.section]  || false) || !!effective[f.code];
    });
    const secExposed = [], secHidden = [];
    (matrix.sections || []).forEach(s => {
      if (!beforeSec[s.code] && afterSec[s.code]) secExposed.push(s);
      if ( beforeSec[s.code] && !afterSec[s.code]) secHidden.push(s);
    });
    return { exposedCodes, hiddenCodes, secExposed, secHidden,
              total: exposedCodes.length + hiddenCodes.length };
  }, [matrix, initialEffective, effective]);

  if (!matrix) return null;
  const bts = matrix.business_types || [];
  const labels = matrix.labels || {};
  // Group features by section for a compact accordion-style grid
  const bySection = {};
  (matrix.features || []).forEach(f => {
    if (!bySection[f.section]) bySection[f.section] = [];
    bySection[f.section].push(f);
  });
  const secOrder = (matrix.sections || []).map(s => s.code);
  const secLabel = Object.fromEntries((matrix.sections || []).map(s => [s.code, s.label]));

  const overrideCount = Object.keys(overrides).length;

  return (
    <Card className="p-4 rounded-sm bg-card border-border space-y-4" data-testid="bt-override-panel">
      <div className="flex items-center gap-2">
        <Sliders className="w-4 h-4 text-orange-600" />
        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">
          Business-type & feature overrides
        </div>
        {overrideCount > 0 && (
          <span className="text-[10px] font-mono uppercase text-amber-700 bg-amber-100 border border-amber-400/25 rounded-sm px-2 py-0.5">
            {overrideCount} override{overrideCount > 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Base business type</div>
          <Select value={bt} onValueChange={setBt}>
            <SelectTrigger className="h-9 rounded-sm bg-background border-border text-foreground" data-testid="bt-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {bts.filter(b => !(matrix.addons || []).includes(b)).map(b =>
                <SelectItem key={b} value={b}>{labels[b] || b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-3 p-2.5 rounded-sm border border-border bg-muted cursor-pointer" data-testid="bt-distro-label">
          <Switch checked={isDistro} onCheckedChange={setIsDistro} data-testid="bt-distro" />
          <span>
            <div className="text-sm font-medium text-foreground">Distribution add-on</div>
            <div className="text-[10px] text-muted-foreground">Layers Distribution-only features on top</div>
          </span>
        </label>
        <label className="flex items-center gap-3 p-2.5 rounded-sm border border-border bg-muted cursor-pointer" data-testid="bt-dealer-label">
          <Switch checked={isDealer} onCheckedChange={setIsDealer} data-testid="bt-dealer" />
          <span>
            <div className="text-sm font-medium text-foreground">Dealership 360 add-on</div>
            <div className="text-[10px] text-muted-foreground">Layers Section J (vehicle · workshop) on top</div>
          </span>
        </label>
      </div>

      <div className="max-h-[420px] overflow-y-auto pr-1 space-y-3 border-t border-border pt-3">
        {/* Iter-195 · Feature search */}
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/80 pointer-events-none" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter features by code or name (e.g. G1 · Manufacturing · BOM)"
            className="pl-8 pr-8 h-8 rounded-sm bg-background border-border text-foreground text-sm"
            data-testid="bt-feature-search" />
          {search && (
            <button onClick={() => setSearch("")} data-testid="bt-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground/80 hover:text-foreground">
              <XIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {(() => {
          const q = search.trim().toLowerCase();
          const totalVisible = q
            ? secOrder.reduce((n, s) => n + (bySection[s] || []).filter(r => r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)).length, 0)
            : (matrix.features || []).length;
          if (q && totalVisible === 0) return (
            <div className="text-center py-6 text-[12px] font-mono text-foreground/45" data-testid="bt-search-empty">
              No features match “{search}”.
            </div>
          );
          return secOrder.map(sec => {
            const all = bySection[sec] || [];
            const rows = q ? all.filter(r => r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)) : all;
            if (rows.length === 0) return null;
            const onCount = rows.filter(r => effective[r.code]).length;
            return (
              <div key={sec} className="rounded-sm border border-border bg-muted/70">
                <div className="px-3 py-2 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-foreground/80 border-b border-border">
                  <span>{sec} · {secLabel[sec] || ""}</span>
                  <span className="text-muted-foreground/80">{onCount}/{rows.length} on{q && ` · filtered from ${all.length}`}</span>
                </div>
                <div className="divide-y divide-white/5">
                  {rows.map(row => {
                    const eff = effective[row.code];
                    const hasOverride = Object.prototype.hasOwnProperty.call(overrides, row.code);
                    const baseVal = !!row[bt];
                    return (
                      <div key={row.code} className="px-3 py-2 flex items-center gap-3 text-sm">
                        <span className="text-[10px] font-mono text-muted-foreground/80 w-10">{row.code}</span>
                        <span className="flex-1 text-foreground">{row.name}</span>
                        <span className={`text-[10px] font-mono uppercase tracking-widest ${baseVal ? "text-emerald-300/70" : "text-foreground/30"}`}>
                          matrix: {baseVal ? "Y" : "N"}
                        </span>
                        {hasOverride && (
                          <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-100 border border-amber-400/25 rounded-sm px-1.5 py-0.5">
                            override
                          </span>
                        )}
                        <Switch checked={!!eff} onCheckedChange={() => toggleOverride(row.code, eff)}
                          data-testid={`bt-toggle-${row.code}`} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          });
        })()}
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-border">
        <Button onClick={save} disabled={busy}
          className="rounded-sm bg-orange-500 hover:bg-orange-500/90 text-foreground gap-1.5" data-testid="bt-save">
          <Save className="w-4 h-4" /> {busy ? "Saving…" : (diff.total > 0 ? `Save · ${diff.total} change${diff.total > 1 ? "s" : ""}` : "Save changes")}
        </Button>
        <Button onClick={resetOverrides} variant="outline"
          className="rounded-sm border-border text-foreground hover:bg-muted gap-1.5" data-testid="bt-reset">
          <RotateCcw className="w-4 h-4" /> Clear all overrides
        </Button>
        <div className="text-[11px] font-mono text-muted-foreground ml-auto">
          Overrides beat matrix + add-ons. Highest precedence.
        </div>
      </div>

      {/* Iter-190 · Effective-features diff preview */}
      {diff.total > 0 && (
        <div className="rounded-sm border border-orange-500/40 bg-orange-500/5 p-4 space-y-3" data-testid="bt-diff-preview">
          <div className="flex items-center gap-2">
            <ArrowRight className="w-4 h-4 text-orange-600" />
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">
              Preview · before → after
            </div>
            <div className="ml-auto text-[11px] font-mono text-muted-foreground">
              {diff.exposedCodes.length} feature{diff.exposedCodes.length !== 1 ? "s" : ""} exposed ·{" "}
              {diff.hiddenCodes.length} hidden
              {diff.secExposed.length + diff.secHidden.length > 0 && (
                <> · {diff.secExposed.length + diff.secHidden.length} section change{diff.secExposed.length + diff.secHidden.length !== 1 ? "s" : ""}</>
              )}
            </div>
          </div>
          {(diff.secExposed.length > 0 || diff.secHidden.length > 0) && (
            <div className="grid md:grid-cols-2 gap-3">
              {diff.secExposed.length > 0 && (
                <div className="rounded-sm border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="diff-sections-exposed">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-600 mb-1.5">
                    <Eye className="w-3 h-3" /> Sections newly visible
                  </div>
                  <ul className="text-sm text-foreground space-y-0.5">
                    {diff.secExposed.map(s => <li key={s.code}><span className="font-mono text-muted-foreground mr-1.5">{s.code}</span>{s.label}</li>)}
                  </ul>
                </div>
              )}
              {diff.secHidden.length > 0 && (
                <div className="rounded-sm border border-red-500/30 bg-red-500/5 p-3" data-testid="diff-sections-hidden">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-red-600 mb-1.5">
                    <EyeOff className="w-3 h-3" /> Sections newly hidden
                  </div>
                  <ul className="text-sm text-foreground space-y-0.5">
                    {diff.secHidden.map(s => <li key={s.code}><span className="font-mono text-muted-foreground mr-1.5">{s.code}</span>{s.label}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            {diff.exposedCodes.length > 0 && (
              <details className="rounded-sm border border-emerald-500/20 bg-muted/70 p-2.5" data-testid="diff-features-exposed">
                <summary className="cursor-pointer text-[11px] font-mono uppercase tracking-widest text-emerald-300/85">
                  Features exposed ({diff.exposedCodes.length})
                </summary>
                <ul className="mt-2 text-[12px] text-foreground/90 space-y-0.5 max-h-40 overflow-y-auto">
                  {diff.exposedCodes.map(c =>
                    <li key={c.code}><span className="font-mono text-foreground/45 mr-2">{c.code}</span>{c.name}</li>)}
                </ul>
              </details>
            )}
            {diff.hiddenCodes.length > 0 && (
              <details className="rounded-sm border border-red-500/20 bg-muted/70 p-2.5" data-testid="diff-features-hidden">
                <summary className="cursor-pointer text-[11px] font-mono uppercase tracking-widest text-red-300/85">
                  Features hidden ({diff.hiddenCodes.length})
                </summary>
                <ul className="mt-2 text-[12px] text-foreground/90 space-y-0.5 max-h-40 overflow-y-auto">
                  {diff.hiddenCodes.map(c =>
                    <li key={c.code}><span className="font-mono text-foreground/45 mr-2">{c.code}</span>{c.name}</li>)}
                </ul>
              </details>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
